import { useMemo } from "react"

import { useAPIHealth } from "shared/api/health/useAPIHealth"
import { useEvent } from "shared/lib/useEvent"
import { Floor } from "shared/types/Floor"

import { getProductTotal } from "entities/projects/lib/getProductTotal"
import { useProjectChildPosition } from "entities/projects/model/useProjectChildPosition"
import { useSharedFloor } from "features/floor-management/model/useSharedFloor"
import { useProjectStore } from "features/project-management/model/useProjectStore"
import { useFloorForm } from "widgets/floor/floor-form/model/useFloorForm"

import { ProjectPageFloorSidebarModel } from "../ProjectPageFloorSidebar"

export interface UseSharedProjectPageFloorSidebarModelArgs {
  projectID: string
  floor: Floor
}

export function useSharedProjectPageFloorSidebarModel({
  projectID,
  floor
}: UseSharedProjectPageFloorSidebarModelArgs): ProjectPageFloorSidebarModel {
  const {
    pendingElementIDs,
    sidebarIsEditing,
    sidebarActiveTab,
    enterSidebarEditing,
    exitSidebarEditing,
    setSidebarActiveTab
  } = useProjectStore(projectID)
  const isPending = pendingElementIDs.includes(floor.id)

  const { price: isPriceServiceAvailable } = useAPIHealth()

  const floorModel = useSharedFloor({
    floorID: floor.id,
    projectID,
    options: { includePrice: true }
  })

  const handleSubmit = useEvent(() => undefined)

  const { displayPosition } = useProjectChildPosition({
    child: floor,
    projectID
  })

  const floorFormModel = useFloorForm({
    floor,
    projectID,
    parentID: projectID,
    displayPosition,
    model: floorModel,
    onSubmit: handleSubmit
  })

  const productTotal = useMemo(
    () => getProductTotal(floor.children),
    [floor.children]
  )

  const { data: priceData, isLoading: isLoadingPrice = false } =
    floorModel.pricing.data.price ?? {}

  return {
    projectID,
    floor,
    price: isPriceServiceAvailable ? priceData : null,
    isLoadingPrice: isPriceServiceAvailable ? isLoadingPrice : false,
    isPriceServiceAvailable,
    productTotal,
    isPending,
    isDisabled: false,
    floorFormModel,
    callbacks: {
      onEdit: undefined,
      onDelete: undefined,
      onDuplicate: undefined,
      onCopy: undefined,
      onAddClarificationRequest: undefined
    },
    hideControls: true,
    isEditing: sidebarIsEditing,
    enterEditing: enterSidebarEditing,
    exitEditing: exitSidebarEditing,
    sidebarActiveTab,
    setSidebarActiveTab
  }
}
